/**
 * KD-3581 — the experience frames of a composed trip.
 *
 * The redesign renders the composer as a list of experience frames, each
 * wrapping the consecutive days it covers. That grouping is DERIVED here and
 * nowhere else: `days` is neither reordered nor restructured, so day insertion,
 * map focus and the save payload keep reading the exact same array they read
 * before this ticket. A frame is a view over `days`, never a replacement for it.
 *
 * Indices are 0-based — the index in the `days` array, the session convention
 * everywhere past `formatTripFromApi`. `experienceDayIndex` is only ever read
 * relatively (does it keep growing?), never as an absolute rank, because it is
 * 1-based in dmc but is only ever compared to its own previous value here.
 */

export type TripExperienceFrame = {
  /** The experience every day of the frame belongs to. */
  experienceId: string;
  /** Index in `days` of the first day of the frame. */
  startDayIndex: number;
  /** Index in `days` of the last day — equal to `startDayIndex` on a single-day frame. */
  endDayIndex: number;
  /** Every index in `days` the frame covers, in order. */
  dayIndices: number[];
};

type ExperienceDay = {
  experienceId?: string | null;
  experienceDayIndex?: number | null;
};

type GroupDaysByExperienceInput = {
  days?: Array<ExperienceDay | null | undefined> | null;
};

/**
 * The frames a trip's days form, in day order.
 *
 * A day carrying no experience opens no frame and belongs to none: the day
 * skeleton is still rendered by the caller, outside of any frame, which is what
 * an empty day and a just-removed experience both look like.
 *
 * The same experience can legitimately be composed twice in a row — two
 * separate frames, not one. The only thing that tells them apart is
 * `experienceDayIndex`: it grows along the days of one instance and restarts on
 * the next, so a value that does not grow closes the frame and opens a new one.
 * Days that do not carry it at all are grouped on `experienceId` alone, which is
 * the behaviour the composer had before the field existed.
 */
export const groupDaysByExperience = ({
  days,
}: GroupDaysByExperienceInput): TripExperienceFrame[] => {
  const frames: TripExperienceFrame[] = [];
  let current: TripExperienceFrame | undefined;
  let previousExperienceDayIndex: number | undefined;

  (Array.isArray(days) ? days : []).forEach((day, dayIndex) => {
    const experienceId = day?.experienceId;

    if (experienceId == null || experienceId === '') {
      // Breaks the run: two frames of the same experience separated by an empty
      // day are two frames, not one frame with a hole in it.
      current = undefined;
      previousExperienceDayIndex = undefined;
      return;
    }

    const experienceDayIndex =
      typeof day?.experienceDayIndex === 'number'
        ? day.experienceDayIndex
        : undefined;

    const startsNewInstance =
      experienceDayIndex != null &&
      previousExperienceDayIndex != null &&
      experienceDayIndex <= previousExperienceDayIndex;

    if (
      current != null &&
      current.experienceId === experienceId &&
      !startsNewInstance
    ) {
      current.endDayIndex = dayIndex;
      current.dayIndices.push(dayIndex);
    } else {
      current = {
        experienceId,
        startDayIndex: dayIndex,
        endDayIndex: dayIndex,
        dayIndices: [dayIndex],
      };
      frames.push(current);
    }

    previousExperienceDayIndex = experienceDayIndex;
  });

  return frames;
};
